import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "../../Components/Grid/Grid";
import Modal from "../../Components/Modal/Rma/Modal";
import Button from "../../Components/button/button";
import "../../Style/IncidentView.css";
import config from "../../config";
import getColumnDefs from "../../utils/Rma/rmaColumnDefs";
import { RmaData } from "../../utils/Rma/rmaTypes";
import { formatDuration } from "../../utils/durationUtils";
import { authFetch } from "../../utils/Security/authFetch";
import { GridApi } from "ag-grid-community";
import localforage from "localforage";

const CACHE_KEY = "incident_cache";
const RmaView: React.FC = () => {
  const userId = localStorage.getItem("userid");
  const isAdmin = localStorage.getItem("isAdmin") === "true";
  const [rowData, setRowData] = useState<RmaData[]>([]);
  const [filteredData, setFilteredData] = useState<RmaData[]>([]);
  const [selectedRma, setSelectedRma] = useState<RmaData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const username = localStorage.getItem("username") || "System";
  const userTeam = localStorage.getItem("position");
  const [loading, setLoading] = useState(true);
  const CACHE_KEY = `rmas_${isAdmin ? "admin" : userId}`;
  const navigate = useNavigate();
    const isForTelebras = localStorage.getItem("isForTelebras") === "true";
    const isForPetrobras = localStorage.getItem("isForPetrobras") === "true";
    const isForNilesat = localStorage.getItem("isForNilesat") === "true";

  // Nilesat admins only see Nilesat RMAs
  const applyNilesatScope = (rows: RmaData[]): RmaData[] =>
    isAdmin && isForNilesat
      ? rows.filter((item: any) =>
          String(item.affectedCustomer ?? "").includes("Nilesat")
        )
      : rows;

  const loadMyTodo = async () => {
    if (!username) return;

    setLoading(true);
    try {
      const res = await authFetch(
        `${config.apiBaseUrl}/api/rmas/my-todo/${username}`
      );
      const data: RmaData[] = applyNilesatScope(await res.json());

      setRowData(data);
      setFilteredData(data);
    } catch (e) {
      console.error("Failed to load My Todo", e);
    } finally {
      setLoading(false);
    }
  };
  // ✅ RESET TO FULL LIST (from cache or API)
  const handleReset = async () => {
    setLoading(true);
    setSearchQuery("");

    try {
      // 1️⃣ Try restoring from cache first
      const cached = await localforage.getItem<{
        rows: RmaData[];
        ts: number;
      }>(CACHE_KEY);

      if (cached?.rows?.length) {
        setRowData(cached.rows);
        setFilteredData(cached.rows);
      } else {
        // 2️⃣ If cache is empty, force reload from API
        window.location.reload();
      }
    } catch (e) {
      console.error("Reset failed, reloading page", e);
      window.location.reload();
    } finally {
      setLoading(false);
    }
  };

  // ✅ MY GROUP TODO
  const loadMyGroupTodo = async () => {
    if (!userTeam) return;

    setLoading(true);
    try {
      const res = await authFetch(
        `${config.apiBaseUrl}/api/rmas/my-group-todo/${userTeam}`
      );
      const data: RmaData[] = applyNilesatScope(await res.json());

      setRowData(data);
      setFilteredData(data);
    } catch (e) {
      console.error("Failed to load Group Todo", e);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (data: RmaData) => {
    navigate(`/rma-edit/${data.ticketId}`, { state: { rma: data } });
  };

  const handleDelete = async (ticketId: string) => {
    if (!window.confirm("Are you sure you want to delete this rma?")) return;

    try {
      const response = await authFetch(
        `${config.apiBaseUrl}/api/rmas/${ticketId}`,
        {
          method: "DELETE",
        }
      );

      if (response.ok) {
        const updatedData = rowData.filter((rma) => rma.ticketId !== ticketId);
        setRowData(updatedData);
        setFilteredData(updatedData.filter(applySearchFilter));
      } else {
        console.error("Failed to delete rma");
      }
    } catch (error) {
      console.error("Error deleting rma:", error);
    }
  };

  useEffect(() => {
    localforage.removeItem(CACHE_KEY);
    const fetchRmas = async () => {
      try {
        const url = isAdmin
          ? `${config.apiBaseUrl}/api/rmas`
          : `${config.apiBaseUrl}/api/rmas/user/${userId}`;

        const response = await authFetch(url);
        if (response.ok) {
          const data: RmaData[] = await response.json();

          const formatted = applyNilesatScope(
            data.map((item) => ({
              ...item,
            }))
          );

          setRowData(formatted);
          setFilteredData(formatted);
        } else {
          console.error("Failed to fetch rmas");
        }
      } catch (error) {
        console.error("Error fetching rmas:", error);
      }
    };

    fetchRmas();
  }, [isAdmin, userId]);

  const columnDefs = getColumnDefs(handleEdit, handleDelete);

  const handleRowClick = (data: RmaData) => {
    setSelectedRma(data);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRma(null);
  };

  const gridApiRef = useRef<GridApi | null>(null);
  const stripHtml = (html: string): string => {
    const div = document.createElement("div");

    const htmlWithImagePlaceholder = html.replace(/<img[^>]*>/gi, "[Image]");

    div.innerHTML = htmlWithImagePlaceholder;
    return div.textContent || div.innerText || "";
  };
  const handleExportCSV = () => {
    if (!gridApiRef.current) {
      alert("Grid is not ready.");
      return;
    }

    const filteredNodes: RmaData[] = [];
    gridApiRef.current.forEachNodeAfterFilter((node) => {
      if (node.data) {
        filteredNodes.push(node.data);
      }
    });

    if (!filteredNodes.length) {
      alert("No data to export.");
      return;
    }

    const exportableColumns = columnDefs.filter((col) => !!col.field);
    const headers = exportableColumns.map((col) => col.headerName || col.field);
    const fields = exportableColumns.map((col) => col.field as keyof RmaData);

    const sanitizeField = (value: any, field: string): string => {
      if (typeof value !== "string") return String(value ?? "");
      if (["summary", "description", "solution"].includes(field)) {
        return stripHtml(value);
      }
      if (/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
        return value.replace("T", " ");
      }
      return value;
    };

    const csvRows = [
      headers.join(","),
      ...filteredNodes.map((row) =>
        fields
          .map((field) => JSON.stringify(sanitizeField(row[field], field)))
          .join(",")
      ),
    ];

    const blob = new Blob(["﻿" + csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Rma.csv";
    a.click();
    URL.revokeObjectURL(url);
  };
  const applySearchFilter = (item: RmaData): boolean => {
    const query = searchQuery.toLowerCase();
    return Object.values(item).some(
      (value) =>
        value !== null &&
        value !== undefined &&
        value.toString().toLowerCase().includes(query)
    );
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setFilteredData(rowData.filter(applySearchFilter));
  };

  return (
    <>
      <div className="top">
        <div className="add">
          <div className="right-section">
          {  !isForTelebras && !isForPetrobras &&(
            <Button
              variant="primary"
              size="md"
              onClick={() => navigate("/rma-add")}
            >
              + Add
            </Button>
          )}
            <Button variant="primary" size="md" onClick={handleExportCSV}>
              Export to CSV
            </Button>
            <Button variant="primary" size="md" onClick={loadMyTodo}>
              My To-Do List
            </Button>

            <Button variant="primary" size="md" onClick={loadMyGroupTodo}>
              My Group To-Do List
            </Button>
            <Button variant="primary" size="md" onClick={handleReset}>
              Reset
            </Button>
            <input
              type="text"
              placeholder="Full Search..."
              value={searchQuery}
              onChange={handleSearchChange}
              style={{
                padding: "8px",
                borderRadius: "5px",
                border: "1px solid gray",
                width: "400px",
              }}
            />
          </div>
        </div>
      </div>

      <div className="Grid" style={{ height: "82%" }}>
        <Grid<RmaData>
          rowData={filteredData}
          columnDefs={columnDefs}
          onGridReady={(params: { api: GridApi<any> | null }) => {
            gridApiRef.current = params.api;
          }}
          onRowClick={handleRowClick}
        />
      </div>

      <Modal isOpen={isModalOpen} data={selectedRma} onClose={closeModal} />
    </>
  );
};

export default RmaView;
